const GA_SCRIPT_ID = "eterniq-ga4-script";

export const PUBLIC_ANALYTICS_PATHS = [
  "/",
  "/register",
  "/userguide",
  "/about",
  "/aboutfull",
  "/features",
  "/contactus",
  "/verified",
  "/plans",
  "/faqs",
  "/customertestimonials",
  "/termsofservice",
  "/privacypolicy",
] as const;

const PUBLIC_ANALYTICS_PATH_SET = new Set<string>(
  PUBLIC_ANALYTICS_PATHS.map((path) => path.toLowerCase()),
);

type AnalyticsEventParams = Record<
  string,
  string | number | boolean | null | undefined
>;

type AnalyticsState = {
  initialized: boolean;
  lastPageViewKey: string | null;
};

const analyticsState: AnalyticsState = {
  initialized: false,
  lastPageViewKey: null,
};

const SAFE_EVENT_PARAM_KEYS = new Set([
  "method",
  "surface",
  "source",
  "placement",
  "section",
  "intent",
]);

function getEnvValue(...keys: string[]): string {
  for (const key of keys) {
    const value = process.env[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
}

export function getAnalyticsMeasurementId(): string {
  return getEnvValue(
    "REACT_APP_GA_MEASUREMENT_ID",
    "VITE_GA_MEASUREMENT_ID",
  );
}

export function isAnalyticsEnabled(): boolean {
  const enabled = getEnvValue("REACT_APP_ENABLE_GA", "VITE_ENABLE_GA");
  const measurementId = getAnalyticsMeasurementId();
  const isEnabled = enabled.toLowerCase() === "true" && !!measurementId;

  return isEnabled;
}

export function normalizeAnalyticsPath(pathname: string): string {
  const trimmed = pathname.trim().toLowerCase();
  if (!trimmed) return "/";

  const [pathOnly] = trimmed.split(/[?#]/);
  const withLeadingSlash = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;
  if (withLeadingSlash === "/") return "/";

  const normalized = withLeadingSlash.replace(/\/+$/, "");
  return normalized || "/";
}

export function isPublicAnalyticsPath(pathname: string): boolean {
  return PUBLIC_ANALYTICS_PATH_SET.has(normalizeAnalyticsPath(pathname));
}

function ensureWindowGlobals(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function ensureDataLayer(): void {
  window.dataLayer = window.dataLayer || [];

  if (typeof window.gtag !== "function") {
    window.gtag = function gtag(): void {
      window.dataLayer?.push(arguments);
    };
  }
}

function injectScript(measurementId: string): void {
  if (document.getElementById(GA_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement("script");
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    measurementId,
  )}`;
  document.head.appendChild(script);
}

function sanitizeEventParams(
  params: AnalyticsEventParams,
): Record<string, string | number | boolean> {
  return Object.entries(params).reduce<Record<string, string | number | boolean>>(
    (acc, [key, value]) => {
      if (!SAFE_EVENT_PARAM_KEYS.has(key)) {
        return acc;
      }

      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed) acc[key] = trimmed;
        return acc;
      }

      if (
        typeof value === "number" ||
        typeof value === "boolean"
      ) {
        acc[key] = value;
      }

      return acc;
    },
    {},
  );
}

export function initializeAnalytics(): boolean {
  const measurementId = getAnalyticsMeasurementId();
  const enabled = isAnalyticsEnabled();

  if (!ensureWindowGlobals() || !enabled) {
    return false;
  }

  if (!measurementId) {
    return false;
  }

  ensureDataLayer();

  if (!analyticsState.initialized) {
    injectScript(measurementId);
    window.gtag?.("js", new Date());
    window.gtag?.("config", measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    analyticsState.initialized = true;
  }

  return true;
}

export function trackPageView(pathname: string, pageTitle?: string): void {
  const normalizedPath = normalizeAnalyticsPath(pathname);
  const isPublicRoute = isPublicAnalyticsPath(normalizedPath);

  if (!isPublicRoute) {
    return;
  }

  if (!initializeAnalytics()) {
    return;
  }

  const title = (pageTitle ?? document.title ?? "").trim();
  const canonicalLocation = `${window.location.origin}${normalizedPath}`;
  const measurementId = getAnalyticsMeasurementId();
  const pageViewKey = `${normalizedPath}::${title}`;

  if (analyticsState.lastPageViewKey === pageViewKey) {
    return;
  }

  analyticsState.lastPageViewKey = pageViewKey;

  const payload = {
    send_to: measurementId,
    page_path: normalizedPath,
    page_location: canonicalLocation,
    ...(title ? { page_title: title } : {}),
  };

  window.gtag?.("event", "page_view", payload);
}

export function trackEvent(
  eventName: string,
  params: AnalyticsEventParams = {},
): void {
  if (!initializeAnalytics()) {
    return;
  }

  const normalizedEventName = eventName.trim();
  if (!normalizedEventName) {
    return;
  }

  const sanitizedParams = sanitizeEventParams(params);
  if (Object.keys(sanitizedParams).length > 0) {
    window.gtag?.("event", normalizedEventName, sanitizedParams);
    return;
  }

  window.gtag?.("event", normalizedEventName);
}

export function resetAnalyticsState(): void {
  analyticsState.initialized = false;
  analyticsState.lastPageViewKey = null;

  if (typeof document !== "undefined") {
    document.getElementById(GA_SCRIPT_ID)?.remove();
  }

  if (typeof window !== "undefined") {
    delete window.gtag;
    delete window.dataLayer;
  }
}
